import { observer } from "mobx-react-lite"
import { Link } from "react-router-dom"

import type { ChainConfig } from "@/domains/core"
import type { ILiquidityPool } from "@/domains/pools"
import type { ManualClPoolStore } from "@/domains/thick-v2/manual-cl-store"
import { Button } from "@/components/button"
import { TableCell, TableRow } from "@/components/table"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/tooltip"
import { cn, dollarsPrettified } from "@/domains/library"
import { PoolInfoColumn } from "@/domains/pools/components/pool-info-column"
import { TvlColumn } from "@/domains/pools/components/tvl-column"
import { AprBadge } from "@/domains/thick-v2/components/apr-badge"
import { RangeAprTooltipBody } from "@/domains/thick-v2/components/range-apr-tooltip-body"

const MAX_LIST_APR_PCT = 9001

/**
 * Row inside the Manual-CL pool-list table. Every numeric value reads from
 * `ManualClPoolStore` computeds — `volumeUsd24h`, `feesUsd24h`,
 * `rangeApr7d`, `displayApr`, `tvlUsdSparkline`, `tvlUsdDelta24hPct`.
 *
 * The APR cell surfaces `rangeApr7d` — a range-aware projection that
 * assumes a position sized to the 7-day high/low (the same dashed lines
 * the create flow draws on the liquidity chart) paired with 7 days of
 * fees annualised by `365/7`. The pool-wide 24h-fees APR is kept around
 * as the tooltip's "full-range baseline" so users can see both numbers
 * side by side. When the 7-day path is unavailable (brand-new pool,
 * stale indexer) the helper falls back to a symmetric ±2% window and the
 * tooltip labels the number as a fallback so the user never mistakes it
 * for real history.
 *
 * When the pool has no interval data yet (brand-new pool, indexer just
 * caught up) the relevant computeds resolve to zero / empty strings and
 * the row renders `—` placeholders instead of pretending to have real
 * figures.
 */
export const ManualClTableRow = observer(
  ({
    pool,
    chainConfig,
    isCompact,
    isFirstItem,
  }: {
    pool: ILiquidityPool
    chainConfig: ChainConfig
    isCompact: boolean
    isFirstItem: boolean
  }) => {
    if (pool.tokens.length < 2) {
      return null
    }

    const manualClPool = pool as ManualClPoolStore
    const rangeAprResult = manualClPool.rangeApr7d
    const formattedRangeApr = capAprPctDisplay(rangeAprResult.aprPct, rangeAprResult.formattedPct)
    const formattedBaselineApr = capAprPctDisplay(rangeAprResult.baselineAprPct, rangeAprResult.formattedBaselinePct)
    const fullRangeApr = capAprStringDisplay(manualClPool.displayApr)

    return (
      <TableRow className={cn(!isFirstItem && "border-t border-t-p400-light")}>
        <TableCell>
          <PoolInfoColumn pool={pool} chainSlug={chainConfig.slug} />
        </TableCell>
        <TableCell className="text-right">
          <TvlColumn pool={pool} isCompact={isCompact} />
          <TvlDeltaLabel deltaPct={manualClPool.tvlUsdDelta24hPct} />
        </TableCell>
        <TableCell className="text-right">
          <UsdMetricText state={manualClPool.volumeUsd24hState} />
        </TableCell>
        <TableCell className="text-right">
          <UsdMetricText state={manualClPool.feesUsd24hState} />
        </TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex cursor-help">
                  <AprBadge value={formattedRangeApr} variant="pool" />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">
                <RangeAprTooltipBody
                  rangeAprResult={rangeAprResult}
                  fullRangeApr={fullRangeApr}
                  formattedBaselinePct={formattedBaselineApr}
                />
              </TooltipContent>
            </Tooltip>
          </div>
        </TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end">
            <TvlSparkline state={manualClPool.tvlUsdSparklineState} />
          </div>
        </TableCell>
        <TableCell className="text-center">
          <div className="mx-auto flex w-fit items-center gap-2">
            <Button variant="default" asChild size="md">
              <Link to={`/liquidity/cl/v2/${pool.address.toString()}`}>Details</Link>
            </Button>
            <Button variant="neutral" asChild size="md">
              <Link to={`/liquidity/cl/v2/${pool.address.toString()}/create-position`}>Add</Link>
            </Button>
          </div>
        </TableCell>
      </TableRow>
    )
  },
)

function capAprPctDisplay(aprPct: number, formattedPct: string): string {
  if (!formattedPct) return formattedPct
  if (!Number.isFinite(aprPct)) return formattedPct
  if (aprPct <= MAX_LIST_APR_PCT) return formattedPct
  return `${MAX_LIST_APR_PCT}%`
}

function capAprStringDisplay(formattedPct: string): string {
  if (!formattedPct) return formattedPct
  const aprPct = Number.parseFloat(formattedPct.replace("%", ""))
  return capAprPctDisplay(aprPct, formattedPct)
}

function TvlDeltaLabel({ deltaPct }: { deltaPct: number | undefined }) {
  if (deltaPct === undefined || !Number.isFinite(deltaPct)) return null
  if (deltaPct <= 0) return null
  return <span className={cn("text-[11px] text-primary-400")}>+{deltaPct.toFixed(2)}% 24h</span>
}

function UsdMetricText({ state }: { state: ManualClPoolStore["volumeUsd24hState"] }) {
  if (state.status === "ready") {
    return <p className="text-t100">{dollarsPrettified(state.value)}</p>
  }
  return <p className="text-xs text-t300">No pricing</p>
}

function TvlSparkline({ state }: { state: ManualClPoolStore["tvlUsdSparklineState"] }) {
  const values = state.values
  if (state.status !== "ready" || values.length < 2) {
    return <span className="text-xs text-t300">{state.label}</span>
  }
  const width = 80
  const height = 22
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const step = width / (values.length - 1)
  const path = values
    .map((v, i) => {
      const x = i * step
      const y = height - ((v - min) / span) * height
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`
    })
    .join(" ")
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} className="text-primary-400" aria-hidden>
      <path d={path} fill="none" stroke="currentColor" strokeWidth={1.25} />
    </svg>
  )
}
